import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Button,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getStudentAssignments } from '../../services/lessonService';
import { useAuth } from '../../hooks/useAuth';

interface Assignment {
  assignment_id: string;
  module_id: string;
  module_title: string;
  module_subject: string;
  module_grade_level: string;
  status: string;
  due_date: string | null;
  assigned_at: string;
  completed_at: string | null;
  instructions: string | null;
}

export const MyAssignments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadAssignments();
    }
  }, [user]);

  const loadAssignments = async () => {
    if (!user) return;

    try {
      setLoading(true);
      // Use 'me' - backend will resolve to authenticated user's DynamoDB user_id
      const result = await getStudentAssignments('me');
      setAssignments(result.assignments || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon fontSize="small" />;
      case 'in_progress':
        return <PendingIcon fontSize="small" />;
      default:
        return <AssignmentIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `Overdue by ${Math.abs(diffDays)} day(s)`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else {
      return `Due in ${diffDays} day(s)`;
    }
  };

  const isDueSoon = (dueDate: string | null) => {
    if (!dueDate) return false;
    const date = new Date(dueDate);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;
  };

  const isOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    const date = new Date(dueDate);
    const now = new Date();
    return date < now;
  };

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          My Assignments
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and complete your assigned lessons
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Assignments Grid */}
      {assignments.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No assignments yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your teacher will assign lessons to you soon
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {assignments.map((assignment) => (
            <Grid item xs={12} sm={6} md={4} key={assignment.assignment_id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  border: isOverdue(assignment.due_date) ? '2px solid' : 'none',
                  borderColor: 'error.main',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
                onClick={() => navigate(`/student/lessons/${assignment.module_id}`)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Chip
                      icon={getStatusIcon(assignment.status)}
                      label={assignment.status.replace('_', ' ')}
                      color={getStatusColor(assignment.status) as any}
                      size="small"
                    />
                    <Chip label={assignment.module_subject} size="small" variant="outlined" />
                  </Box>

                  <Typography variant="h6" gutterBottom>
                    {assignment.module_title}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Grade {assignment.module_grade_level}
                  </Typography>

                  {assignment.due_date && (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={formatDate(assignment.due_date)}
                        size="small"
                        color={
                          isOverdue(assignment.due_date)
                            ? 'error'
                            : isDueSoon(assignment.due_date)
                            ? 'warning'
                            : 'default'
                        }
                      />
                    </Box>
                  )}

                  {assignment.instructions && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Instructions:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {assignment.instructions.length > 100
                          ? `${assignment.instructions.substring(0, 100)}...`
                          : assignment.instructions}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant={assignment.status === 'completed' ? 'outlined' : 'contained'}
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/student/lessons/${assignment.module_id}`);
                      }}
                    >
                      {assignment.status === 'completed' ? 'Review Lesson' : 'Start Lesson'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default MyAssignments;
